import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FiUser, FiClock, FiUsers, FiPower, FiChevronDown, FiChevronRight, FiLock, FiBell } from 'react-icons/fi';
import { fetchUnreadCount } from '../../../store/slices/notificationSlice';
import { logoutUser } from '../../../store/slices/authSlice';
import { showSuccessToast, showErrorToast } from '../../../utils/messageHelper';

const ProfileSidebar = () => {
  const [profileExpanded, setProfileExpanded] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  
  // Get unread notification count from Redux
  const { unreadCount } = useSelector((state) => state.notifications);

  // Fetch unread count on mount
  useEffect(() => {
    dispatch(fetchUnreadCount());
  }, [dispatch]);

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      showSuccessToast('auth.logoutSuccess');
      navigate('/');
    } catch (error) {
      showErrorToast('general.somethingWentWrong');
    }
  };

  return (
    <div className="w-full lg:w-96 flex-shrink-0 bg-white rounded-lg shadow-jet overflow-hidden">
      {/* Yellow Header */}
      <div className="rounded-t-[calc(0.25rem-1px)] flex min-h-[2.6rem] items-center py-[0.2rem] px-[1.1em] mb-0 bg-[#ffd200] border-b border-white">
        <h3 className="text-black text-lg leading-[1.2] font-semibold mb-0">My Account</h3>
      </div>
      
      <div className="p-0">
        {/* Profile Section with Dropdown */}
        <div className="border-b border-gray-200">
          <div 
            className={`flex items-center justify-between py-4 px-5 cursor-pointer transition-colors duration-200 border-l-4 ${profileExpanded ? 'border-l-[#ed7d31] bg-white' : 'border-l-transparent bg-white'}`}
            onClick={() => setProfileExpanded(!profileExpanded)}
          >
            <div className="flex items-center gap-3">
              <FiUser size={16} style={{ color: profileExpanded ? '#ed7d31' : '#111236', fontWeight: '600' }} />
              <span className={`text-base font-semibold leading-[25px] ${profileExpanded ? 'text-[#ed7d31]' : 'text-[#111236]'}`}>Profile</span>
            </div>
            {profileExpanded ? (
              <FiChevronDown size={16} style={{ color: '#ed7d31', fontWeight: '600' }} />
            ) : (
              <FiChevronRight size={16} style={{ color: '#111236', fontWeight: '600' }} />
            )}
          </div>
          {profileExpanded && (
            <div className="bg-white">
              <div 
                className={`flex items-center gap-3 py-3 px-5 pl-14 cursor-pointer transition-colors duration-200 border-l-4 border-b border-gray-200 ${isActive('/jetsetter/profile/user-details') ? 'border-l-[#ed7d31] text-[#ed7d31]' : 'border-l-transparent text-[#4a5568]'}`}
                onClick={() => navigate('/jetsetter/profile/user-details')}
              >
                <FiUser size={16} style={{ color: isActive('/jetsetter/profile/user-details') ? '#ed7d31' : '#111236', fontWeight: '600' }} />
                <span className={`text-[13px] font-semibold leading-[25px] ${isActive('/jetsetter/profile/user-details') ? 'text-[#ed7d31]' : 'text-[#111236]'}`}>User Details</span>
              </div>
              <div 
                className={`flex items-center gap-3 py-3 px-5 pl-14 cursor-pointer transition-colors duration-200 border-l-4 ${isActive('/jetsetter/profile/change-password') ? 'border-l-[#ed7d31] text-[#ed7d31]' : 'border-l-transparent text-[#4a5568]'}`}
                onClick={() => navigate('/jetsetter/profile/change-password')}
              >
                <FiLock size={16} style={{ color: isActive('/jetsetter/profile/change-password') ? '#ed7d31' : '#111236', fontWeight: '600' }} />
                <span className={`text-[13px] font-semibold leading-[25px] ${isActive('/jetsetter/profile/change-password') ? 'text-[#ed7d31]' : 'text-[#111236]'}`}>Change Password</span>
              </div>
            </div>
          )}
        </div>
        
        {/* Booking History */}
        <div 
          className={`flex items-center justify-between py-4 px-5 cursor-pointer transition-colors duration-200 border-b border-gray-200 border-l-4 ${isActive('/jetsetter/profile/booking-history') ? 'border-l-[#ed7d31] text-[#ed7d31]' : 'border-l-transparent text-[#4a5568]'}`}
          onClick={() => navigate('/jetsetter/profile/booking-history')}
        >
          <div className="flex items-center gap-3">
            <FiClock size={16} style={{ color: isActive('/jetsetter/profile/booking-history') ? '#ed7d31' : '#111236', fontWeight: '600' }} />
            <span className={`text-[13px] font-semibold leading-[25px] ${isActive('/jetsetter/profile/booking-history') ? 'text-[#ed7d31]' : 'text-[#111236]'}`}>Booking History</span>
          </div>
        </div>
        
        {/* My Passengers */}
        <div 
          className={`flex items-center justify-between py-4 px-5 cursor-pointer transition-colors duration-200 border-b border-gray-200 border-l-4 ${isActive('/jetsetter/profile/my-passengers') ? 'border-l-[#ed7d31] text-[#ed7d31]' : 'border-l-transparent text-[#4a5568]'}`}
          onClick={() => navigate('/jetsetter/profile/my-passengers')}
        >
          <div className="flex items-center gap-3">
            <FiUsers size={16} style={{ color: isActive('/jetsetter/profile/my-passengers') ? '#ed7d31' : '#111236', fontWeight: '600' }} />
            <span className={`text-base font-semibold leading-[25px] ${isActive('/jetsetter/profile/my-passengers') ? 'text-[#ed7d31]' : 'text-[#111236]'}`}>My Passengers</span>
          </div>
        </div>
        
        {/* Messages */}
        <div 
          className={`flex items-center justify-between py-4 px-5 cursor-pointer transition-colors duration-200 border-b border-gray-200 border-l-4 ${isActive('/jetsetter/profile/messages') ? 'border-l-[#ed7d31] text-[#ed7d31]' : 'border-l-transparent text-[#4a5568]'}`}
          onClick={() => navigate('/jetsetter/profile/messages')}
        >
          <div className="flex items-center gap-3">
            <FiBell size={16} style={{ color: isActive('/jetsetter/profile/messages') ? '#ed7d31' : '#111236', fontWeight: '600' }} />
            <span className={`text-base font-semibold leading-[25px] ${isActive('/jetsetter/profile/messages') ? 'text-[#ed7d31]' : 'text-[#111236]'}`}>Messages</span>
          </div>
          <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-full min-w-[1.5rem] h-6 flex items-center justify-center">{unreadCount > 99 ? '99+' : unreadCount}</span>
        </div>
        
        {/* Logout Button */}
        <div className="p-4" onClick={() => handleLogout()}>
          <button className="w-full bg-[#ffd200] text-black text-[13px] font-bold py-2 px-4 rounded-md flex items-center justify-center gap-3 cursor-pointer transition-all duration-200 hover:bg-[#e6bd00] border-none">
            <FiPower size={16} style={{ color: '#111236', fontWeight: '600' }} />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileSidebar;